'use client';

import React, { useState } from 'react';
import Button from '@/components/ui/button';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { cn, COUNTRY_LOCALE_MAP } from '@/lib/utils';
import { addContactToCustomerIO } from '@/actions/addContactToCustomerIO';
import { usePostHog } from 'posthog-js/react';
import { useParams } from 'next/navigation';

const NewsLetter = () => {
  const params = useParams<{ countryCode?: string }>();
  const countryCode =
    (Array.isArray(params?.countryCode)
      ? params.countryCode[0]
      : params?.countryCode) || 'au';
  const locale = COUNTRY_LOCALE_MAP[countryCode] || 'en-AU';

  const {
    register,
    getValues,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  register('email', {
    required: 'Invalid email address',
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      message: 'Invalid email address',
    },
  });
  const email = getValues('email');

  const labelClassName =
    'absolute text-[16px] font-proxima_font duration-300 transform -translate-y-3 scale-75 top-3 z-10 origin-[0] start-5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-3 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto pointer-events-none';

  const [isLoading, setIsLoading] = useState(false);
  const posthog = usePostHog();

  const onSubmit = async (data: any) => {
    setIsLoading(true);
    try {
      const result = await addContactToCustomerIO({
        email: data.email,
        first_name: data.firstName,
        last_name: data.lastName,
        country_code: locale,
      });

      console.log(result);
      if (result) {
        toast.success('Success! You have been subscribed.', {
          position: 'bottom-center',
        });
        reset();
        posthog?.capture('Newsletter_SignUp');
      } else {
        toast.error("You're already subscribed to our newsletter.", {
          position: 'bottom-center',
        });
      }
    } catch (error) {
      toast.error("You're already subscribed to our newsletter.", {
        position: 'bottom-center',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const [isChecked, setIsChecked] = useState(true);

  const toggleCheckbox = () => {
    setIsChecked(!isChecked);
  };

  return (
    <div className="min-w-0 rounded-lg md:px-5">
      <div className="flex flex-col gap-5 lg:flex-row justify-end py-4 md:py-8">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex min-w-0 flex-col gap-3 md:gap-[12px]"
        >
          <div className="flex md:flex-row flex-col  gap-[16px] w-full">
            <div className="w-full md:w-[395px] md:max-lg:min-w-0 md:max-lg:max-w-[395px] md:max-lg:flex-1 md:max-lg:w-auto">
              <div
                className={`relative w-full text-black/90 font-medium ${errors['email'] && email ? 'warning-input' : ''}`}
              >
                <input
                  id="email"
                  type="email"
                  className={`block rounded-[12px] px-5 h-[48px] pb-2.5 pt-5 w-full bg-white appearance-none peer border-2 outline-none focus:outline-none ${errors['email'] && email ? 'border-red-500' : 'border-[#DFE1E7] '} `}
                  placeholder=" "
                  {...register('email', {
                    required: 'Invalid email address',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address.',
                    },
                  })}
                />
                <label
                  style={{ color: 'black' }}
                  htmlFor={'email'}
                  className={labelClassName}
                >
                  Email address...
                </label>
              </div>
              {errors['email'] && (
                <p className="text-xs flex items-center gap-1 font-medium text-red-500 mt-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    fill="red"
                    viewBox="0 0 256 256"
                    className="shrink-0"
                  >
                    <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm-8-80V80a8,8,0,0,1,16,0v56a8,8,0,0,1-16,0Zm20,36a12,12,0,1,1-12-12A12,12,0,0,1,140,172Z"></path>
                  </svg>

                  {errors['email'].message as string}
                </p>
              )}
            </div>
            <Button
              label="Sign Up"
              variant="fill"
              className="text-[16px] !border-black font-semibold font-proxima_font w-full md:w-[120px] md:max-lg:shrink-0 h-[48px] !text-white bg-black select-none whitespace-nowrap !leading-none"
              disabled={isLoading}
            />
          </div>

          <div className="flex items-center gap-2 group  cursor-pointer font-medium pl-2 ">
            <span
              className={cn(
                'w-[16px] h-[16px] flex-shrink-0 flex items-center justify-center border-[2.5px] border-[#DFE1E7] cursor-pointer rounded-[3px] transition duration-300',
                isChecked && 'bg-black border-transparent'
              )}
              onClick={toggleCheckbox}
            >
              {isChecked && (
                <svg
                  className="w-3 h-3 text-white fill-current"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path d="M0 11l2-2 5 5L18 3l2 2L7 18z" />
                </svg>
              )}
            </span>
            <label
              onClick={toggleCheckbox}
              htmlFor="remember"
              className="cursor-pointer text-off-black text-[14px] font-proxima_font select-none"
            >
              You agree to receive newsletters and marketing emails from UNIMIG
            </label>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewsLetter;
